import { CaseWorkerRole, CaseWorkerStatus } from '@repo/shared/dtos/caseWorker'
import { DataProvider } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@repo/ui/hoverCard'
import { InlineCode } from '@repo/ui/inlineCode'
import { Switch } from '@repo/ui/switch'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { Cfl, cn } from '@repo/ui/utils'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'

import { DataTableColumnHeader } from '@/components/admin/members/ColHeader'
import { BeneficiaryLink } from '@/components/beneficiary/BeneficiaryLink'
import { RootState } from '@/store'
import { Beneficiary } from '@/store/beneficiary'

// FIXME: i18n
export const columns: (p: {
  onAssignOrUnassign: (p: { assign: boolean; beneficiaryId: string }) => void
  onSelectBeneficiary?: () => void
}) => ColumnDef<Beneficiary>[] = ({ onAssignOrUnassign, onSelectBeneficiary }) => {
  const { tc } = useT()
  return useMemo(
    () => [
      {
        id: 'id',
        header: () => <div>{tc('assign', { cfl: true })}</div>,
        cell: ({ row }) => {
          return (
            <TooltipWrapper content="Assign">
              <Switch
                checked={row.getIsSelected()}
                tabIndex={-1}
                onCheckedChange={(value) => {
                  row.toggleSelected(!!value) // Optimistic update
                  onAssignOrUnassign({ assign: !!value, beneficiaryId: row.original.id })
                }}
              />
            </TooltipWrapper>
          )
        },
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: 'fullName',
        enableHiding: false,
        meta: { defaultHidden: false, label: 'fullName', type: 'string' },
        header: ({ column }) => (
          <DataTableColumnHeader
            title={tc('fullName', { cfl: true })}
            column={column}
            options={{ sortable: true }}
          />
        ),
        cell: ({ row }) => (
          <BeneficiaryLink
            onClick={onSelectBeneficiary}
            id={row.original.id}
            fullName={row.original.fullName}
          />
        ),
      },
      {
        accessorKey: 'profile.value',
        meta: { defaultHidden: false, label: 'profile', type: 'string' },
        header: ({ column }) => (
          <DataTableColumnHeader title={tc('profile', { cfl: true })} column={column} />
        ),
        cell: ({ column, row }) => {
          const value = row.getValue<string>(column.id)
          return (
            <HoverCard>
              <HoverCardTrigger asChild>
                <div className="line-clamp-1 max-w-sm select-none whitespace-pre-wrap break-words">
                  {value}
                </div>
              </HoverCardTrigger>
              <HoverCardContent className="w-96">{value}</HoverCardContent>
            </HoverCard>
          )
        },
      },
      {
        accessorKey: 'caseWorkerIds',
        header: ({ column }) => {
          return (
            <DataTableColumnHeader title={tc('membersAssigned', { cfl: true })} column={column} />
          )
        },
        cell: ({ column, row }) => {
          const caseWorkerIds = row.getValue<string[]>(column.id)
          return (
            <DataProvider
              selectors={[(state: RootState) => state.admin.caseWorkers]}
              render={(caseWorkers) => {
                const cws = caseWorkers.filter(({ id }) => caseWorkerIds.includes(id))
                if (cws.length === 0) return null
                return (
                  <div className="inline-flex flex-wrap gap-2">
                    {cws.map((cw) => (
                      <span key={cw.id}>
                        <TooltipWrapper content={`${cw.email} • (${Cfl(cw.role)})`}>
                          <InlineCode
                            className={cn(
                              'text-xs bg-primary-400 text-primary-50',
                              cw.status === CaseWorkerStatus.Disabled && 'opacity-70',
                            )}
                          >
                            {cw.firstName} {cw.lastName}
                            {cw.status === CaseWorkerStatus.Disabled && (
                              <span className="font-medium"> (disabled)</span>
                            )}
                            {cw.role === CaseWorkerRole.Admin && (
                              <span className="font-medium"> (admin)</span>
                            )}
                          </InlineCode>
                        </TooltipWrapper>
                      </span>
                    ))}
                  </div>
                )
              }}
            />
          )
        },
      },
    ],
    [],
  )
}
